import * as React from 'react'
import type { ProposalChapter } from '@lib/enums/proposal-chapter.enum'
import type { ProposalPlan } from '@lib/enums/proposal-plan.enum'
import {
  activityEventSchema,
  type ActivityEvent,
} from '@lib/input/proposal-activity.input'
import { proposalService } from '@/services/ProposalService'
import logger, { toErrorMessage } from '@/helpers/logger'

/**
 * What a client does on their shared proposal, sent back for the salesperson.
 *
 * A signal, not a record: which chapters were read and for how long, the plans
 * tried, a tree opened, a video watched. Events queue here and
 * go in small batches, every few seconds and as the tab is hidden, so reading
 * the proposal never waits on it. A batch that fails is dropped: losing a few
 * seconds of reading is better than retrying into a page that is closing.
 *
 * Only the shared page provides this. The staff preview and the wording
 * preview get the default, which records nothing, so staff looking at a
 * proposal never show up as the client reading it.
 */

/**
 * What the page reports.
 *
 * One object per kind, so a chapter cannot be passed to a plan and an open
 * cannot be given a sentence. A budget is no longer typed anywhere, so
 * BUDGET is not sent; the kind stays for the events already recorded.
 */
export type TrackCall =
  | { kind: 'READ'; detail: ProposalChapter; seconds: number }
  | { kind: 'PLAN'; detail: ProposalPlan; seconds?: number | null }
  | { kind: 'OPENED' | 'SHARED' | 'PRESENTED' }
  | { kind: 'PRINTED'; detail?: 'pdf' | null }
  | { kind: 'TREE' | 'VIDEO'; detail: string }
  | { kind: 'ADJUSTED'; detail: string; seconds?: number | null }

export type Track = (event: TrackCall) => void

const TEXT_LIMIT = 160
const SECONDS_LIMIT = 3600

/** Seconds the API will store: a whole number from 0 to one hour, or none. */
function clampSeconds(value: number | null | undefined): number | null {
  if (value == null) return null
  return Math.max(0, Math.min(SECONDS_LIMIT, Math.round(value)))
}

/**
 * The object one kind sends.
 *
 * Built from the call, so a strict schema is not handed fields that kind
 * does not have. Text is cut to the limit the schema allows. A budget is
 * not built here: nothing sends one, and cutting a dollar amount would
 * store a different number. The kind stays on the schema for events already
 * recorded.
 */
function activityPayload(event: TrackCall, occurredAt: string) {
  switch (event.kind) {
    case 'OPENED':
    case 'SHARED':
    case 'PRESENTED':
      return { kind: event.kind, occurredAt }
    case 'PRINTED':
      return {
        kind: event.kind,
        detail: event.detail === 'pdf' ? 'pdf' : null,
        occurredAt,
      }
    case 'READ':
      return {
        kind: event.kind,
        detail: event.detail,
        seconds: clampSeconds(event.seconds) ?? 0,
        occurredAt,
      }
    case 'PLAN':
      return {
        kind: event.kind,
        detail: event.detail,
        seconds: clampSeconds(event.seconds),
        occurredAt,
      }
    case 'TREE':
    case 'VIDEO':
      return {
        kind: event.kind,
        detail: event.detail.slice(0, TEXT_LIMIT),
        seconds: null,
        occurredAt,
      }
    case 'ADJUSTED':
      return {
        kind: event.kind,
        detail: event.detail.slice(0, TEXT_LIMIT),
        seconds: clampSeconds(event.seconds),
        occurredAt,
      }
    default: {
      const unreachable: never = event
      return unreachable
    }
  }
}

const ActivityContext = React.createContext<Track>(() => undefined)

export function useTrack(): Track {
  return React.useContext(ActivityContext)
}

const FLUSH_EVERY_MS = 10_000
const BATCH_LIMIT = 50

export function ActivityProvider({
  token,
  children,
}: {
  token: string
  children: React.ReactNode
}) {
  const queue = React.useRef<ActivityEvent[]>([])
  const hasOpened = React.useRef(false)

  const flush = React.useCallback(() => {
    if (queue.current.length === 0) return
    const events = queue.current.splice(0, BATCH_LIMIT)
    proposalService.recordActivityShared(token, { events }).catch((caught) => {
      logger.warn('Could not record proposal activity', {
        error: toErrorMessage(caught),
      })
    })
  }, [token])

  React.useEffect(() => {
    const timer = window.setInterval(flush, FLUSH_EVERY_MS)
    const onHidden = (): void => {
      if (window.document.visibilityState === 'hidden') flush()
    }
    window.document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', flush)
    return () => {
      window.clearInterval(timer)
      window.document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [flush])

  const track = React.useMemo<Track>(() => {
    const report: Track = (event) => {
      // One open per page load, however often the page asks to record it.
      if (event.kind === 'OPENED') {
        if (hasOpened.current) return
        hasOpened.current = true
      }
      const parsed = activityEventSchema.safeParse(
        activityPayload(event, new Date().toISOString()),
      )
      if (!parsed.success) {
        logger.warn('Dropped a proposal activity event the schema refused', {
          kind: event.kind,
          error: parsed.error.issues.map((issue) => issue.message).join('; '),
        })
        return
      }
      queue.current.push(parsed.data)
      if (queue.current.length >= BATCH_LIMIT) flush()
    }
    return report
  }, [flush])

  return <ActivityContext.Provider value={track}>{children}</ActivityContext.Provider>
}

/**
 * Time spent on each chapter, reported when the reader moves on or hides
 * the tab. A glance of under two seconds is scrolling past, not reading.
 * While `isPaused` (the slides are up over the document) nothing counts: the
 * room is watching the presentation, not reading the chapter beneath it.
 */
/**
 * What dwell timing reports. Only a READ: the hook never emits another kind,
 * so it does not take the whole {@link Track}.
 */
export type ChapterRead = (event: Extract<TrackCall, { kind: 'READ' }>) => void

export function useChapterDwell(
  active: ProposalChapter | null,
  track: ChapterRead,
  isPaused = false,
): void {
  const current = React.useRef<{ id: ProposalChapter; since: number | null } | null>(null)

  const close = React.useCallback(() => {
    const segment = current.current
    if (segment?.since == null) return
    const seconds = (Date.now() - segment.since) / 1000
    if (seconds >= 2) track({ kind: 'READ', detail: segment.id, seconds })
    segment.since = null
  }, [track])

  React.useEffect(() => {
    close()
    current.current =
      active === null
        ? null
        : {
            id: active,
            since: !isPaused && window.document.visibilityState === 'visible' ? Date.now() : null,
          }
  }, [active, isPaused, close])

  React.useEffect(() => {
    const onVisibility = (): void => {
      if (window.document.visibilityState === 'hidden') {
        close()
      } else if (current.current !== null && !isPaused) {
        current.current.since = Date.now()
      }
    }
    window.document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.document.removeEventListener('visibilitychange', onVisibility)
      close()
    }
  }, [isPaused, close])
}
